import { useEffect, useMemo, useState } from 'react';
import { SonataLI } from 'sonata-li';
import { useDidUpdate } from 'sonata-react-components';
import { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import { usePOIImporter } from '@/components/common/POIImporter/POIImporter.context';
import { useInsertionOrderFormContext } from '@/components/ioSettings/InsertionOrderForm/InsertionOrderForm.provider';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { useStore as useIOSettingsStore } from '@/store/local/ioSettings';
import { useStore as useLineItemStore } from '@/store/local/lineItem';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { MultiFlightMapContainerStyled } from '../../MultiFlightMap.style';
import { Lists } from '../Lists/Lists';
import { MapPopoverProvider } from '../MapPopover/MapPopover.context';
import { MapView } from '../MapView';
import { LiStyled } from './Content.styles';

import type { ReactNode } from 'react';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { IoSettingsStore } from '@/store/local/ioSettings';
import type { LineItemStore } from '@/store/local/lineItem';
import type { MapMode, ModifiedScreen } from '../MapView/map.types';

type Props = {
  mapId: string;
  origin: MultiFlightEntityType;
  volumetryAndForecast?: ReactNode;
};

const AUDIENCE_VIEW_TOOL_MODE: MapMode = 'audience_view_tool';
const DEFAULT_CAMPAIGN_BOUNDS = '-4.28466796875,40.25332852309839,-3.09539794921875,40.6462615921222';

type ModifiedVenue = Venue<string> & { id: number };

function normalizeItems(items: Partial<ModifiedScreen>[] | ModifiedVenue[]) {
  return items.map(item => ({
    ...structuredClone(item),
    ...(item.id?.toString && { _id: item.id.toString() })
  }));
}

function ContentIO({ mapId, volumetryAndForecast }: Omit<Props, 'origin'>) {
  const { entity } = useIOSettingsStore() as IoSettingsStore;
  const {
    addItemsFromSonataLI,
    currentSection,
    mode,
    onAddBatchVenues,
    onInventoryChange,
    onSave,
    setMode,
    user,
    userRoles,
    ...rest
  } = useMultiFlightMap();
  const { venuesAdded, uploadProcess } = usePOIImporter();

  const { isSnliActive, screenInventoryExportIds, setIsSnliActive, setScreenGroupsLayer } =
    useInsertionOrderFormContext();
  const entityJSON = entity.toJSON();
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode) {
      setMode(null);
    }
  }, [currentSection]);

  useEffect(() => {
    setIsSnliActive(mode === AUDIENCE_VIEW_TOOL_MODE);
  }, [mode]);

  useEffect(() => {
    if (!isSnliActive && mode === AUDIENCE_VIEW_TOOL_MODE) {
      setMode(null);
    }
  }, [isSnliActive]);

  const handleLIClose = async () => {
    setMode(null);
  };

  useDidUpdate(() => {
    if (venuesAdded.length > 0 && uploadProcess === 'venueMapUpload') {
      onAddBatchVenues(venuesAdded);
    }
  }, [venuesAdded, uploadProcess]);

  const normalizedScreens = useMemo(() => {
    return normalizeItems(rest.temporaryScreens);
  }, [rest.temporaryScreens]);

  const normalizedVenues = useMemo(() => {
    return normalizeItems(rest.temporaryVenues);
  }, [rest.temporaryVenues]);

  return mode === AUDIENCE_VIEW_TOOL_MODE ? (
    <LiStyled>
      <SonataLI
        campaignBounds={{ toBBoxString: () => DEFAULT_CAMPAIGN_BOUNDS }}
        entity="insertionOrder"
        entityData={entityJSON}
        entityId={entityJSON._id}
        locations={rest.temporaryZones}
        onClose={handleLIClose}
        onExportVenuesAndLocations={addItemsFromSonataLI}
        onInventoryChange={onInventoryChange}
        onScreenGroupLayerChange={setScreenGroupsLayer}
        platform="sonata-v2"
        screenInventoryExportIds={screenInventoryExportIds}
        screens={normalizedScreens}
        user={user}
        userRoles={userRoles}
        venues={normalizedVenues}
      />
    </LiStyled>
  ) : (
    <>
      <Lists mapId={mapId} />
      <MapPopoverProvider boundaryElement={mapContainer}>
        <MultiFlightMapContainerStyled ref={setMapContainer}>
          <MapView
            id={mapId}
            navigationPosition="top-right"
            style="streets"
            volumetryAndForecast={volumetryAndForecast}
          />
        </MultiFlightMapContainerStyled>
      </MapPopoverProvider>
    </>
  );
}

function ContentLI({ mapId, volumetryAndForecast }: Omit<Props, 'origin'>) {
  const { entity } = useLineItemStore() as LineItemStore;
  const {
    addItemsFromSonataLI,
    currentSection,
    mode,
    onAddBatchVenues,
    onInventoryChange,
    onSave,
    setMode,
    user,
    userRoles,
    ...rest
  } = useMultiFlightMap();

  const { venuesAdded, uploadProcess } = usePOIImporter();

  const { isSnliActive, screenInventoryExportIds, setIsSnliActive, setScreenGroupsLayer } = useLineItemFormContext();
  const entityJSON = entity.toJSON();
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode) {
      setMode(null);
    }
  }, [currentSection]);

  useEffect(() => {
    setIsSnliActive(mode === AUDIENCE_VIEW_TOOL_MODE);
  }, [mode]);

  useEffect(() => {
    if (!isSnliActive && mode === AUDIENCE_VIEW_TOOL_MODE) {
      setMode(null);
    }
  }, [isSnliActive]);

  const handleLIClose = async () => {
    setMode(null);
  };

  useDidUpdate(() => {
    if (venuesAdded.length > 0 && uploadProcess === 'venueMapUpload') {
      onAddBatchVenues(venuesAdded);
    }
  }, [venuesAdded, uploadProcess]);

  const normalizedScreens = useMemo(() => {
    return normalizeItems(rest.temporaryScreens);
  }, [rest.temporaryScreens]);

  const normalizedVenues = useMemo(() => {
    return normalizeItems(rest.temporaryVenues);
  }, [rest.temporaryVenues]);

  return mode === AUDIENCE_VIEW_TOOL_MODE ? (
    <LiStyled>
      <SonataLI
        campaignBounds={{ toBBoxString: () => DEFAULT_CAMPAIGN_BOUNDS }}
        entity="lineItem"
        entityData={entityJSON}
        entityId={entityJSON._id}
        locations={rest.temporaryZones}
        onClose={handleLIClose}
        onExportVenuesAndLocations={addItemsFromSonataLI}
        onInventoryChange={onInventoryChange}
        onScreenGroupLayerChange={setScreenGroupsLayer}
        platform="sonata-v2"
        screenInventoryExportIds={screenInventoryExportIds}
        screens={normalizedScreens}
        user={user}
        userRoles={userRoles}
        venues={normalizedVenues}
      />
    </LiStyled>
  ) : (
    <>
      <Lists mapId={mapId} />
      <MapPopoverProvider boundaryElement={mapContainer}>
        <MultiFlightMapContainerStyled ref={setMapContainer}>
          <MapView
            id={mapId}
            navigationPosition="top-right"
            style="streets"
            volumetryAndForecast={volumetryAndForecast}
          />
        </MultiFlightMapContainerStyled>
      </MapPopoverProvider>
    </>
  );
}

export function Content({ mapId, origin, volumetryAndForecast }: Props) {
  return origin === 'insertionOrder' ? (
    <ContentIO mapId={mapId} volumetryAndForecast={volumetryAndForecast} />
  ) : (
    <ContentLI mapId={mapId} volumetryAndForecast={volumetryAndForecast} />
  );
}
